import { supabase } from "@/integrations/supabase/client";

// Types for Land & Expand services
export interface ExpansionMetrics {
  growthOpportunities: number;
  avgExpansionReadiness: number;
  topTierClients: number;
  expansionContacts: number;
}

export interface GrowthOpportunity {
  id: string;
  name: string;
  currentServices: number;
  totalServices: number;
  growthPotential: number;
  expansionReadiness: "High" | "Medium" | "Low";
}

export interface ExpansionOpportunity {
  id: string;
  name: string;
  organizationId: string;
  organizationName: string;
  type: string;
  value: number;
  stage: string;
  expectedCloseDate: string | null;
  parentId: string | null;
}

export interface ServiceGap {
  id: string;
  name: string;
  category: string;
  status: string;
  startDate?: string;
  renewalDate?: string;
  fitScore?: number;
  recommendation?: string;
}

export interface ServiceGapsResponse {
  organizationId: string;
  organizationName: string;
  services: ServiceGap[];
}

export interface ClientGrowth {
  organizationId: string;
  priorityRanking: number;
  growthPotential: number;
  currentServices: number;
  targetServices: number;
  expansionReadiness: string;
  lastReviewDate: string | null;
  nextReviewDate: string | null;
  strategyNotes: string | null;
}

export interface WhitespaceAnalysisItem {
  name: string;
  value: number;
}

export interface GrowthTrendItem {
  quarter: string;
  totalServices: number;
  newServices: number;
}

// Fetch expansion metrics
export const fetchExpansionMetrics = async (): Promise<ExpansionMetrics> => {
  try {
    // Count growth opportunities
    const { count: growthCount, error: growthError } = await supabase
      .from('client_growth')
      .select('*', { count: 'exact', head: true });

    // Get top tier clients (tier 1 and 2)
    const { count: tierCount, error: tierError } = await supabase
      .from('organizations')
      .select('*', { count: 'exact', head: true })
      .in('tier', ['1', '2']);
    
    // Calculate average expansion readiness (this would normally be more sophisticated)
    const { data: readinessData, error: readinessError } = await supabase
      .from('client_growth')
      .select('expansion_readiness');
    
    // Get expansion contacts (contacts marked with high influence)
    const { count: contactsCount, error: contactsError } = await supabase
      .from('contacts')
      .select('*', { count: 'exact', head: true })
      .eq('influence_level', 'high');
    
    // Calculate average readiness
    let avgReadiness = 0;
    if (readinessData && readinessData.length > 0) {
      const readinessMapping = {
        'high': 90,
        'medium': 60,
        'low': 30
      };
      
      const validReadinessItems = readinessData.filter(item => 
        item.expansion_readiness && 
        ['high', 'medium', 'low'].includes(item.expansion_readiness.toLowerCase())
      );
      
      if (validReadinessItems.length > 0) {
        const totalReadiness = validReadinessItems.reduce((sum, item) => {
          const readiness = item.expansion_readiness.toLowerCase();
          return sum + (readinessMapping[readiness as keyof typeof readinessMapping] || 0);
        }, 0);
        
        avgReadiness = Math.round(totalReadiness / validReadinessItems.length);
      } else {
        avgReadiness = 0;
      }
    }
    
    // Return combined metrics
    return {
      growthOpportunities: growthCount || 0,
      avgExpansionReadiness: avgReadiness,
      topTierClients: tierCount || 0,
      expansionContacts: contactsCount || 0
    };
  } catch (error) {
    return {
      growthOpportunities: 0,
      avgExpansionReadiness: 0,
      topTierClients: 0,
      expansionContacts: 0
    };
  }
};

// Fetch growth opportunities
export const fetchGrowthOpportunities = async (): Promise<GrowthOpportunity[]> => {
  try {
    // Get organizations with client_growth data
    const { data, error } = await supabase
      .from('client_growth')
      .select(`
        organization_id,
        priority_ranking,
        expansion_readiness,
        current_services,
        target_services,
        growth_potential,
        organizations (id, name)
      `)
      .order('priority_ranking', { ascending: true })
      .limit(5);
    
    if (error) throw error;
    
    // Format the data for the frontend
    return (data || []).map(item => {
      const org = item.organizations as { id: string; name: string };
      
      // Ensure we have numeric values
      const currentServices = typeof item.current_services === 'string' 
        ? parseInt(item.current_services) 
        : (item.current_services || 0);
        
      const totalServices = typeof item.target_services === 'string' 
        ? parseInt(item.target_services) 
        : (item.target_services || 0);
      
      // Calculate growth potential as percentage with bounds check
      const growthPotential = totalServices > 0 
        ? Math.min(100, Math.max(0, Math.round(((totalServices - currentServices) / totalServices) * 100)))
        : 0;
      
      // Normalize expansion readiness
      let expansionReadiness = "Low";
      if (item.expansion_readiness) {
        const readiness = item.expansion_readiness.toLowerCase();
        if (readiness === 'high' || readiness === 'medium' || readiness === 'low') {
          expansionReadiness = readiness.charAt(0).toUpperCase() + readiness.slice(1) as "High" | "Medium" | "Low";
        }
      }
      
      return {
        id: org.id,
        name: org.name,
        currentServices,
        totalServices,
        growthPotential,
        expansionReadiness
      };
    });
  } catch (error) {
    return [];
  }
};

// Fetch expansion opportunities
export const fetchExpansionOpportunities = async (): Promise<ExpansionOpportunity[]> => {
  try {
    // Get expansion opportunities from dedicated table
    const { data: expansionData, error: expansionError } = await supabase
      .from('expansion_opportunities')
      .select(`
        id,
        organization_id,
        expansion_type,
        potential_value,
        probability,
        expected_conversion_date,
        status,
        priority,
        notes,
        organizations (id, name)
      `)
      .in('status', ['In Progress', 'Planning', 'Opportunity', 'active'])
      .order('expected_conversion_date', { ascending: true });
    
    if (expansionError) throw expansionError;
    
    // Also get regular opportunities marked as expansion type
    const { data: regularExpansion, error: regularError } = await supabase
      .from('opportunities')
      .select(`
        id,
        name,
        organization_id,
        value,
        stage,
        expected_close_date,
        opportunity_type,
        parent_opportunity_id,
        organizations (id, name)
      `)
      .or('opportunity_type.eq.expansion,parent_opportunity_id.not.is.null')
      .order('expected_close_date', { ascending: true });
    
    if (regularError) throw regularError;
    
    // Combine both sources
    const allOpportunities: ExpansionOpportunity[] = [];
    
    // Process expansion opportunities table
    if (expansionData) {
      expansionData.forEach(item => {
        const org = item.organizations as { id: string; name: string };
        allOpportunities.push({
          id: item.id,
          name: `${item.expansion_type} Expansion`,
          organizationId: org.id,
          organizationName: org.name,
          type: item.expansion_type || 'expansion',
          value: item.potential_value || 0,
          stage: item.status,
          expectedCloseDate: item.expected_conversion_date,
          parentId: null
        });
      });
    }
    
    // Process regular opportunities
    if (regularExpansion) {
      regularExpansion.forEach(item => {
        const org = item.organizations as { id: string; name: string };
        allOpportunities.push({
          id: item.id,
          name: item.name,
          organizationId: org.id,
          organizationName: org.name,
          type: item.opportunity_type || 'standard',
          value: item.value || 0,
          stage: item.stage,
          expectedCloseDate: item.expected_close_date,
          parentId: item.parent_opportunity_id
        });
      });
    }
    
    // Sort by expected close date
    return allOpportunities.sort((a, b) => {
      if (!a.expectedCloseDate) return 1;
      if (!b.expectedCloseDate) return -1;
      return new Date(a.expectedCloseDate).getTime() - new Date(b.expectedCloseDate).getTime();
    });
  } catch (error) {
    return [];
  }
};

// Fetch whitespace analysis
export const fetchWhitespaceAnalysis = async (): Promise<WhitespaceAnalysisItem[]> => {
  try {
    // Get service usage distribution across organizations
    const { data: services, error: servicesError } = await supabase
      .from('organization_services')
      .select(`
        organization_id
      `);
    
    if (servicesError) throw servicesError;
    
    // Count services per organization
    const serviceCountMap = new Map<string, number>();
    
    if (services) {
      services.forEach(service => {
        const orgId = service.organization_id;
        serviceCountMap.set(orgId, (serviceCountMap.get(orgId) || 0) + 1);
      });
    }
    
    // Group by buckets
    const noServices = Array.from(serviceCountMap.values()).filter(count => count === 0).length;
    const oneToTwoServices = Array.from(serviceCountMap.values()).filter(count => count >= 1 && count <= 2).length;
    const threeToFourServices = Array.from(serviceCountMap.values()).filter(count => count >= 3 && count <= 4).length;
    const fivePlusServices = Array.from(serviceCountMap.values()).filter(count => count >= 5).length;
    
    // Get total organizations to account for those with no services
    const { count: totalOrgs, error: orgError } = await supabase
      .from('organizations')
      .select('*', { count: 'exact', head: true });
    
    if (orgError) throw orgError;
    
    // Calculate orgs with no services
    const orgsWithNoServices = (totalOrgs || 0) - serviceCountMap.size;
    
    return [
      { name: "No Services", value: orgsWithNoServices },
      { name: "1-2 Services", value: oneToTwoServices },
      { name: "3-4 Services", value: threeToFourServices },
      { name: "5+ Services", value: fivePlusServices }
    ];
  } catch (error) {
    return [];
  }
};

// Fetch growth trends
export const fetchGrowthTrends = async (): Promise<GrowthTrendItem[]> => {
  try {
    // Get services with start dates
    const { data: services, error: servicesError } = await supabase
      .from('organization_services')
      .select(`
        start_date
      `)
      .not('start_date', 'is', null);
    
    if (servicesError) throw servicesError;
    
    // Group services by quarter
    const quarterMap = new Map<string, { total: number, new: number }>();
    const now = new Date();
    const currentYear = now.getFullYear();
    
    // Initialize with recent quarters
    for (let i = 1; i <= 4; i++) {
      quarterMap.set(`Q${i} ${currentYear-1}`, { total: 0, new: 0 });
    }
    for (let i = 1; i <= 4; i++) {
      quarterMap.set(`Q${i} ${currentYear}`, { total: 0, new: 0 });
    }
    
    // Process service start dates
    if (services) {
      services.forEach(service => {
        if (!service.start_date) return;
        
        const startDate = new Date(service.start_date);
        const year = startDate.getFullYear();
        const quarter = Math.floor(startDate.getMonth() / 3) + 1;
        const quarterKey = `Q${quarter} ${year}`;
        
        // Only include recent quarters (last year and this year)
        if (year >= currentYear - 1) {
          // Increment new services for this quarter
          const quarterData = quarterMap.get(quarterKey) || { total: 0, new: 0 };
          quarterData.new += 1;
          quarterMap.set(quarterKey, quarterData);
          
          // Increment total for this and all future quarters
          for (const [qKey, qData] of quarterMap.entries()) {
            const [_, qYear] = qKey.split(' ');
            const [_q, qNum] = qKey.split('Q');
            
            const thisQuarterYear = parseInt(qYear);
            const thisQuarterNum = parseInt(qNum);
            
            if (thisQuarterYear > year || (thisQuarterYear === year && thisQuarterNum >= quarter)) {
              qData.total += 1;
            }
          }
        }
      });
    }
    
    // Convert to array and sort by quarter chronologically
    return Array.from(quarterMap.entries())
      .sort((a, b) => {
        const [aQ, aYear] = a[0].split(' ');
        const [bQ, bYear] = b[0].split(' ');
        const aYear_num = parseInt(aYear);
        const bYear_num = parseInt(bYear);
        const aQ_num = parseInt(aQ.replace('Q', ''));
        const bQ_num = parseInt(bQ.replace('Q', ''));
        
        if (aYear_num !== bYear_num) {
          return aYear_num - bYear_num;
        }
        return aQ_num - bQ_num;
      })
      .map(([quarter, data]) => ({
        quarter,
        totalServices: data.total,
        newServices: data.new
      }));
  } catch (error) {
    return [];
  }
};

// Fetch service gaps for an organization
export const fetchServiceGaps = async (organizationId: string): Promise<ServiceGapsResponse> => {
  try {
    // Get organization details
    const { data: orgData, error: orgError } = await supabase
      .from('organizations')
      .select(`
        id,
        name
      `)
      .eq('id', organizationId)
      .single();
    
    if (orgError) throw orgError;
    
    // Get services currently used by the organization
    const { data: activeServices, error: activeError } = await supabase
      .from('organization_services')
      .select(`
        id,
        status,
        start_date,
        renewal_date,
        services (id, name, category)
      `)
      .eq('organization_id', organizationId);
    
    if (activeError) throw activeError;
    
    // Get all available services
    const { data: allServices, error: allError } = await supabase
      .from('services')
      .select(`
        id,
        name,
        category
      `);
    
    if (allError) throw allError;
    
    // Process real data for service gaps
    const activeServiceIds = new Set(activeServices?.map(s => (s.services as any).id) || []);
    
    const formattedActiveServices = (activeServices || []).map(s => {
      const service = s.services as any;
      return {
        id: service.id,
        name: service.name,
        category: service.category,
        status: 'active',
        startDate: s.start_date,
        renewalDate: s.renewal_date
      };
    });
    
    // Get client growth data for fit scores
    const { data: growthData } = await supabase
      .from('client_growth')
      .select('*')
      .eq('organization_id', organizationId)
      .single();
    
    // Generate fit scores for potential services
    const potentialServices = (allServices || [])
      .filter(s => !activeServiceIds.has(s.id))
      .map(s => {
        // Generate a fit score based on organization data and service characteristics
        // Ideally this would use real algorithms, but for demo we'll use a deterministic approach
        const baseScore = 50;
        let fitScore = baseScore;
        
        // Use hash of service ID and org ID to generate a consistent but seemingly random score
        const combinedId = s.id + organizationId;
        const hash = combinedId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        const hashComponent = hash % 30;
        
        // Add some variation based on growth data if available
        if (growthData) {
          const growthBonus = (growthData.growth_potential || 0) / 5;
          fitScore += growthBonus;
          
          // Higher priority organizations get better fit scores
          if (growthData.priority_ranking < 5) {
            fitScore += 10;
          }
        }
        
        // Add the hash component for variation
        fitScore += hashComponent;
        
        // Ensure within bounds
        fitScore = Math.min(85, Math.max(40, fitScore));
        
        const recommendation = fitScore > 70 ? 'High priority' : 
                              fitScore > 60 ? 'Consider next quarter' : 
                              'Evaluate need';
        
        return {
          id: s.id,
          name: s.name,
          category: s.category,
          status: 'potential',
          fitScore: Math.round(fitScore),
          recommendation
        };
      });
    
    return {
      organizationId,
      organizationName: orgData?.name || '',
      services: [...formattedActiveServices, ...potentialServices]
    };
  } catch (error) {
    return {
      organizationId,
      organizationName: '',
      services: []
    };
  }
};

// Fetch client growth data
export const fetchClientGrowth = async (organizationId: string): Promise<ClientGrowth | null> => {
  try {
    const { data, error } = await supabase
      .from('client_growth')
      .select(`
        organization_id,
        priority_ranking,
        growth_potential,
        current_services,
        target_services,
        expansion_readiness,
        last_review_date,
        next_review_date,
        strategy_notes
      `)
      .eq('organization_id', organizationId)
      .single();
    
    if (error) {
      // If no record exists, return null
      if (error.code === 'PGRST116') {
        return null;
      }
      throw error;
    }
    
    return {
      organizationId: data.organization_id,
      priorityRanking: data.priority_ranking || 0,
      growthPotential: data.growth_potential || 0,
      currentServices: data.current_services || 0,
      targetServices: data.target_services || 0,
      expansionReadiness: data.expansion_readiness || 'Low',
      lastReviewDate: data.last_review_date,
      nextReviewDate: data.next_review_date,
      strategyNotes: data.strategy_notes
    };
  } catch (error) {
    return null;
  }
};

// Fetch top expansion candidates
export const fetchTopExpansionCandidates = async (): Promise<GrowthOpportunity[]> => {
  // Using the same function as fetchGrowthOpportunities for now
  return fetchGrowthOpportunities();
};

// Create expansion opportunity
export const createExpansionOpportunity = async (params: {
  organizationId: string;
  organizationName: string;
  notes: string;
  expansionReadiness: string;
}): Promise<{ id: string }> => {
  try {
    const { organizationId, notes, expansionReadiness } = params;
    
    // Create a probability based on expansion readiness
    const probabilityMap = {
      "High": 70,
      "Medium": 50,
      "Low": 30
    };
    
    const probability = probabilityMap[expansionReadiness as keyof typeof probabilityMap] || 50;
    
    // Create opportunity in database
    const { data, error } = await supabase
      .from('opportunities')
      .insert({
        name: `Expansion: ${params.organizationName}`,
        organization_id: organizationId,
        description: notes,
        opportunity_type: 'expansion',
        probability: probability,
        stage: 'discovery',
        expected_close_date: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 90 days from now
      })
      .select('id')
      .single();
    
    if (error) throw error;
    
    return { id: data.id };
  } catch (error) {
    throw error;
  }
};